// ✅ src/pages/VehicleV2/DayWiseDailyProgressReport.jsx
// FULL UPDATED FILE
// ✅ Uses localStorage session values
// ✅ Sends backend-matching inventory headers
// ✅ Compatible with inventory-pool backend pattern

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaEye,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/dayWiseDailyProgressReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const normalizeOptionList = (arr, fallbackSelect = true) => {
  const list = Array.isArray(arr) ? arr : [];

  const mapped = list.map((x) => {
    if (typeof x === "string") return { label: x, value: x };

    const label =
      x.label ??
      x.fld_branchname ??
      x.fld_BranchName ??
      x.fld_vehicletypename ??
      x.VehicleType ??
      x.value ??
      "";

    const value =
      x.value ??
      x.fld_branchid ??
      x.fld_vehiclemaintypeid ??
      x.fld_vehicletypeindex ??
      x.id ??
      x.label ??
      "";

    return { label: String(label), value: String(value) };
  });

  if (!fallbackSelect) return mapped;

  const hasSelect = mapped.some(
    (o) => toStr(o.value).toLowerCase() === "select"
  );

  return hasSelect
    ? mapped
    : [{ label: "Select", value: "Select" }, ...mapped];
};

const monthOptions = [
  { label: "Select", value: "Select" },
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const dayCols = Array.from({ length: 31 }, (_, i) => String(i + 1));

export default function DayWiseDailyProgressReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- localStorage session ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  // HOUR | KM
  const [mode, setMode] = useState("HOUR");

  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
    years: [{ label: "Select", value: "Select" }],
    months: monthOptions,
  });

  const [filters, setFilters] = useState({
    branch:
      sessionBranchType && sessionBranchType.toUpperCase() !== "HO"
        ? sessionBranchId || "Select"
        : "Select",
    month: "Select",
    year: "Select",
    vehicleType: "Select",
    vehicleCode: "",
    vehicleName: "",
    regNo: "",
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const loadInit = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/init`, {
        headers: apiHeaders,
        params: {
          branchId: sessionBranchId,
          branchType: sessionBranchType,
          loginUserId: userId,
        },
      });

      if (!data?.success) throw new Error(data?.message || "Init failed");

      const branches = normalizeOptionList(data.branches);
      const vehicleTypes = normalizeOptionList(data.vehicleTypes);
      const years = normalizeOptionList(
        (data.years || []).map((y) => ({
          label: String(y),
          value: String(y),
        }))
      );

      setDd((p) => ({
        ...p,
        branches,
        vehicleTypes,
        years: years.length ? years : p.years,
        months: monthOptions,
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load dropdowns");
    }
  };

  const loadList = async (override = {}) => {
    setLoading(true);
    try {
      const f = { ...filters, ...override };

      const params = {
        mode,
        branchId: sessionBranchId,
        branchType: sessionBranchType,
        loginUserId: userId,

        branch: isSelect(f.branch) ? "Select" : f.branch,
        month: isSelect(f.month) ? "Select" : f.month,
        year: isSelect(f.year) ? "Select" : f.year,
        vehicleType: isSelect(f.vehicleType) ? "Select" : f.vehicleType,

        vehicleCode: toStr(f.vehicleCode),
        vehicleName: toStr(f.vehicleName),
        regNo: toStr(f.regNo),
      };

      const { data } = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      if (!data?.success) throw new Error(data?.message || "List failed");

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || (data.rows?.length ?? 0)));
    } catch (err) {
      console.error(err);
      setRows([]);
      setTotal(0);
      toast.error(err?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (rows.length) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleChange = (e) => {
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSearch = async () => {
    await loadList();
    toast.success("Search completed");
  };

  const handleViewAll = async () => {
    const reset = {
      branch:
        sessionBranchType && sessionBranchType.toUpperCase() !== "HO"
          ? sessionBranchId || "Select"
          : "Select",
      month: "Select",
      year: "Select",
      vehicleType: "Select",
      vehicleCode: "",
      vehicleName: "",
      regNo: "",
    };

    setFilters(reset);
    await loadList(reset);
    toast.info("Showing all records");
  };

  const handlePrintAll = () => {
    navigate("/vehicle/DayWiseDailyProgressReport_Print", {
      state: {
        mode,
        filters,
        apiHeaders,
        session: {
          finYearId,
          finYear,
          branchId: sessionBranchId,
          branchType: sessionBranchType,
          userId,
        },
      },
    });
  };

  const getVehicleIdByCode = async (vehicleCode) => {
    const vc = toStr(vehicleCode);
    if (!vc) throw new Error("Vehicle Code is empty");

    const { data } = await axios.get(`${API_BASE}/vehicleIdByCode`, {
      params: { vehicleCode: vc },
      headers: apiHeaders,
    });

    if (!data?.success)
      throw new Error(data?.message || "vehicleIdByCode failed");

    const vehId = data?.vehicleId ?? data?.vehId ?? data?.id ?? 0;
    if (!vehId) throw new Error("Vehicle ID not found for this vehicle code");

    return String(vehId);
  };

  const handleVehicleWise = async (row) => {
    try {
      const vehicleCode =
        row?.VehicleCode ?? row?.vehicleCode ?? row?.fld_vehicle_code ?? "";
      const date = row?.Lastupdatedate ?? "";

      if (!toStr(vehicleCode)) {
        toast.error("Vehicle Code not found in row");
        return;
      }

      const vehId = await getVehicleIdByCode(vehicleCode);

      navigate("/vehicle/DayWiseDailyProgressReportVehicleWise", {
        state: {
          vehId,
          vehicleCode: toStr(vehicleCode),
          progMonth: filters.month,
          progYear: filters.year,
          progDate: date,
          mode,
          filters,
          apiHeaders,
          session: {
            finYearId,
            finYear,
            branchId: sessionBranchId,
            branchType: sessionBranchType,
            userId,
          },
        },
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to open Vehicle Wise report");
    }
  };

  const handleDayWise = (row, dayNo) => {
    const vehicleCode =
      row?.VehicleCode ?? row?.vehicleCode ?? row?.fld_vehicle_code ?? "";

    if (!toStr(vehicleCode)) {
      toast.error("Vehicle Code not found in row");
      return;
    }

    navigate("/vehicle/DayWiseDailyProgressReport_DayWise", {
      state: {
        mode,
        filters,
        vehicleCode,
        dayNo: Number(dayNo),
        apiHeaders,
        session: {
          finYearId,
          finYear,
          branchId: sessionBranchId,
          branchType: sessionBranchType,
          userId,
        },
      },
    });
  };

  const headerLeft = (
    <div className="flex items-center gap-3">
      <h1
        className={`text-2xl font-bold ${
          isDark ? "text-white" : "text-gray-900"
        }`}
      >
        Day Wise Daily Progress Report
      </h1>

      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
        }`}
      >
        {total} Records
      </span>

      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          isDark ? "bg-gray-700 text-gray-100" : "bg-gray-100 text-gray-700"
        }`}
        title="INFRA SCREEN"
      >
        B-64
      </span>
    </div>
  );

  const pageActions = (
    <div className="flex flex-wrap items-center gap-3">
      <button
        onClick={() => setShowFilters((p) => !p)}
        className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
          isDark
            ? "border-gray-600 text-white hover:bg-gray-800" +
              (showFilters ? " bg-gray-800" : "")
            : "border-gray-300 text-gray-700 hover:bg-gray-50" +
              (showFilters ? " bg-gray-50" : "")
        }`}
      >
        <FaFilter className="w-4 h-4" />
        {showFilters ? "Hide Filters" : "Show Filters"}
      </button>

      <button
        onClick={() => navigate(-1)}
        className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
          isDark
            ? "border-gray-600 text-white hover:bg-gray-800"
            : "border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        <FaArrowLeft className="w-4 h-4" />
        Back
      </button>
    </div>
  );

  const get = (r, keys, fallback = "") => {
    for (const k of keys) {
      const v = r?.[k];
      if (v !== null && v !== undefined && toStr(v) !== "") return v;
    }
    return fallback;
  };

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              DAY WISE DAILY PROGRESS REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            {headerLeft}
            {pageActions}
          </div>

          <div className="flex gap-6 mb-6 items-center">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={mode === "HOUR"}
                onChange={() => setMode("HOUR")}
              />
              HOUR
            </label>

            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={mode === "KM"}
                onChange={() => setMode("KM")}
              />
              KM
            </label>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {dd.branches.map((b) => (
                      <option key={`${b.value}`} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select
                    name="month"
                    value={filters.month}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {dd.months.map((m) => (
                      <option key={`${m.value}`} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {dd.years.map((y) => (
                      <option key={`${y.value}`} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <select
                    name="vehicleType"
                    value={filters.vehicleType}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {dd.vehicleTypes.map((v) => (
                      <option key={`${v.value}`} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration No.</label>
                  <input
                    type="text"
                    name="regNo"
                    value={filters.regNo}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle reg no"
                  />
                </div>

                {/* <div className="flex items-end gap-3">
                  <button
                    onClick={handleViewAll}
                    className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <FaEye />
                    View All
                  </button>
                </div> */}
              </div>

              <div className="flex flex-col md:flex-row md:justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handlePrintAll}
                  className={`flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaPrint />
                  Print
                </button>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>

                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>

                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Try adjusting your filters or click "View All".
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Branch
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Vehicle Main Type
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Vehicle Name
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Vehicle Code
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Vehicle RegNo
                      </th>

                      {dayCols.map((d) => (
                        <th
                          key={d}
                          className="px-3 py-3 text-center font-semibold whitespace-nowrap"
                        >
                          {d}
                        </th>
                      ))}

                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Total HR
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Total KM
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Cummulative HR
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Cummulative KM
                      </th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                        Last Updated Date
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${
                          isDark ? "bg-gray-800" : "bg-white"
                        } hover:${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {idx + 1}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, ["Branch", "fld_branchname", "BranchName"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, [
                            "VehicleMainType",
                            "fld_vehiclemaintype",
                            "VehicleType",
                            "fld_vehicletypename",
                          ])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, ["VehicleName", "fld_vehicle_name"])}
                        </td>

                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-300 hover:text-blue-200"
                                : "text-blue-700 hover:text-blue-800"
                            }`}
                            onClick={() => handleVehicleWise(r)}
                            title="Vehicle Wise"
                          >
                            {get(r, [
                              "VehicleCode",
                              "vehicleCode",
                              "fld_vehicle_code",
                            ])}
                          </button>
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, ["VehicleRegNo", "regNo", "fld_vehicle_regno"])}
                        </td>

                        {dayCols.map((d) => {
                          const val = r?.[d] ?? r?.[`D${d}`] ?? "";
                          const isClickable =
                            toStr(val) !== "" && toStr(val) !== "0";

                          return (
                            <td key={d} className="px-3 py-3 text-sm text-center">
                              {isClickable ? (
                                <button
                                  className={`underline ${
                                    isDark
                                      ? "text-white hover:text-gray-200"
                                      : "text-gray-900 hover:text-black"
                                  }`}
                                  onClick={() => handleDayWise(r, d)}
                                  title={`Day ${d} details`}
                                >
                                  {val}
                                </button>
                              ) : (
                                <span
                                  className={`${
                                    isDark ? "text-gray-400" : "text-gray-500"
                                  }`}
                                >
                                  {toStr(val) || ""}
                                </span>
                              )}
                            </td>
                          );
                        })}

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, ["TotalHr", "TotalHR", "TotHr"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, ["TotalKM", "TotKM"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, ["CummulativeHr", "CummulativeHR", "CumHr"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, ["CummulativeKM", "CumKM"])}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {get(r, [
                            "Lastupdatedate",
                            "LastUpdatedDate",
                            "fld_lastupdatedate",
                          ])}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}